import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiSparkles, HiX } from 'react-icons/hi';
import { Button } from 'components/ui';
import { CompanyContext } from 'contexts/CompanyContext';
import { SessionContext } from 'contexts/SessionContext';
import { useWhitelabel } from 'contexts/WhitelabelThemeContext';
import { PriorityModalContext } from 'contexts/PriorityModalContext';
import Subscription, { AI_STUDIO_PLANS } from 'views/subscription';
import {
    SHOW_UPGRADE_BANNER,
    UPGRADE_BANNER_DISMISSED_KEY,
    BANNER_DISMISSAL_DURATION_DAYS,
    UPGRADE_BANNER_CONFIG,
} from 'constants/banner.constant';

/**
 * UpgradeBanner Component
 * 
 * Displays a dismissable banner to promote plan upgrades for AI features.
 * Only shown to:
 * - Main users (company owners)
 * - Non-whitelabel instances
 * - Users without active premium subscriptions
 * 
 * @param {string} page - Current page identifier to check if banner should show
 */
const UpgradeBanner = ({ page = 'dashboard' }) => {
    const { mainUser } = useContext(CompanyContext);
    const { user } = useContext(SessionContext);
    const { isWhitelabel } = useWhitelabel();
    const { openModalPriority } = useContext(PriorityModalContext);
    
    const [isVisible, setIsVisible] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    // Check if banner should be shown
    useEffect(() => {
        const shouldShow = checkShouldShowBanner();
        setIsVisible(shouldShow);
    }, [mainUser?.id, user?.id, isWhitelabel, page]);

    /**
     * Determines if banner should be displayed based on multiple criteria
     */
    const checkShouldShowBanner = () => {
        // Don't show if globally disabled
        if (!SHOW_UPGRADE_BANNER) {
            return false;
        }

        // Don't show if page not in enabled list
        if (!UPGRADE_BANNER_CONFIG.enabledPages.includes(page)) {
            return false;
        }

        // Don't show on whitelabel instances
        if (isWhitelabel) {
            return false;
        }

        // Only show to main users (company owners)
        if (!mainUser?.id || !user?.id || mainUser?.id !== user?.id) {
            return false;
        }

        // Don't show if user has an active premium subscription
        const hasActiveSubscription = mainUser?.subscription?.stripe_status === 'active' 
            || mainUser?.subscription?.stripe_status === 'trialing';
        
        if (hasActiveSubscription) {
            const planName = mainUser?.subscription?.items?.find(item => item.product.type === 'plan')?.product?.name;
            if (AI_STUDIO_PLANS.includes(planName)) {
                return false;
            }
        }

        // Check if banner was recently dismissed
        const dismissalData = localStorage.getItem(UPGRADE_BANNER_DISMISSED_KEY);
        if (dismissalData) {
            try {
                const { dismissedAt } = JSON.parse(dismissalData);
                const dismissedDate = new Date(dismissedAt);
                const daysSinceDismissal = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
                
                if (daysSinceDismissal < BANNER_DISMISSAL_DURATION_DAYS) {
                    return false;
                }
            } catch (error) {
                console.error('Error parsing banner dismissal data:', error);
            }
        }

        return true;
    };

    /**
     * Handles banner dismissal with animation
     */
    const handleDismiss = () => {
        setIsClosing(true);
        
        // Save dismissal timestamp
        const dismissalData = {
            dismissedAt: new Date().toISOString(),
            page: page,
        };
        localStorage.setItem(UPGRADE_BANNER_DISMISSED_KEY, JSON.stringify(dismissalData));

        // Hide banner after animation
        setTimeout(() => {
            setIsVisible(false);
            setIsClosing(false);
        }, 300);
    };

    /**
     * Opens subscription modal
     */
    const handleUpgrade = () => {
        openModalPriority(
            <Subscription isModal={true} defaultTab="ai_studio" />, 
            1200, 
            true, 
            true, 
            `!p-0 !bg-tertiary-100 dark:!bg-gray-900 zoom-95`
        );
    };

    if (!isVisible) {
        return null;
    }

    return (
        <AnimatePresence>
            {!isClosing && (
                <motion.div
                    initial={{ opacity: 0, y: -20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.95 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className="w-full mb-8"
                >
                    <div className="relative overflow-hidden rounded-2xl shadow-2xl shadow-purple-500/20">
                        {/* Animated background gradient */}
                        {/* <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 opacity-50 group-hover:opacity-100 transition-opacity duration-500"></div> */}
                        
                        {/* Content container */}
                        <div className="relative bg-gradient-to-r from-purple-500 to-pink-500 dark:bg-gray-900 rounded-[14px] p-4 sm:p-5">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                {/* Icon and text content wrapper for mobile */}
                                <div className="flex items-start gap-4 flex-1 w-full sm:w-auto">
                                    {/* Icon */}
                                    <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
                                        <HiSparkles className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                                    </div>

                                    {/* Text content */}
                                    <div className="flex-1 min-w-0">
                                        <h3 className="!text-base sm:!text-base font-bold text-white dark:text-white">
                                            {UPGRADE_BANNER_CONFIG.title}
                                        </h3>
                                        <p className="!text-XS text-white dark:text-gray-100 leading-relaxed">
                                            {UPGRADE_BANNER_CONFIG.description}
                                        </p>
                                    </div>
                                </div>

                                {/* Action buttons */}
                                <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto justify-end">
                                    <Button
                                        variant="solid"
                                        size="sm"
                                        onClick={handleUpgrade}
                                        className="!bg-gradient-to-r !from-purple-600 !to-pink-600 hover:!from-purple-700 hover:!to-pink-700 !text-white !shadow-lg !shadow-purple-500/30 hover:!shadow-purple-500/40 transition-all duration-200 whitespace-nowrap"
                                    >
                                        {UPGRADE_BANNER_CONFIG.ctaText}
                                    </Button>
                                    
                                    {/* Dismiss button */}
                                    <button
                                        onClick={handleDismiss}
                                        className="p-2 rounded-lg text-gray-200 hover:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
                                        aria-label="Dismiss banner"
                                    >
                                        <HiX className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default UpgradeBanner;

